import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "next-i18next";

import { ShipdaCurrentLanguage } from "@sellernote/shared/src/i18n/i18nForShipda";
import Seo from "@sellernote/shipda-kr/src/containers/Seo";
import { supportActions } from "@sellernote/shipda-kr/src/modules/support";
import { useAppSelector } from "@sellernote/shipda-kr/src/store";
import BoardPageTitle from "@sellernote/ui/src/sds-v1/components/board/BoardPageTitle";
import Loading from "@sellernote/ui/src/sds-v1/components/Loading";

import { getServerSidePropsWithI18n } from "../../../utils/i18n";

import CommonStyled from "../index.styles";
import SupportBreadcrumb from "../SupportBreadcrumb";
import SupportLayout from "../SupportLayout";
import SupportSideMenu from "../SupportSideMenu";
import BoardSection from "./BoardSection";

export const getServerSideProps = getServerSidePropsWithI18n();

export default function GuideList({
  isSupportMainPage,
}: {
  isSupportMainPage?: boolean; // '/support'에서도 같이 사용하므로 구분하기 위한 flag
}) {
  const { t } = useTranslation(["support-new"]);

  const dispatch = useDispatch();

  const { useList, tradeTermsList, faqList } = useAppSelector((state) => {
    return {
      useList: state.support.GET_HOW_TO_LIST.data?.list,
      tradeTermsList: state.support.GET_TERMS_LIST.data?.list,
      faqList: state.support.GET_FAQ_LIST.data?.list,
    };
  });

  useEffect(() => {
    fetchList();
  }, []);

  function fetchList() {
    const params = { page: 0, perPage: 3 };

    dispatch(supportActions.GET_HOW_TO_LIST(params));
    dispatch(supportActions.GET_TERMS_LIST(params));
    dispatch(supportActions.GET_FAQ_LIST(params));
  }

  const hasRequiredData = useList && tradeTermsList && faqList;

  if (!hasRequiredData)
    return (
      <Loading active={!hasRequiredData} label={t("support-new:공통_로딩")} />
    );

  return (
    <SupportLayout>
      {isSupportMainPage ? (
        <Seo
          path="/support"
          title={
            ShipdaCurrentLanguage.currentLanguage === "ko"
              ? "고객지원 - 디지털 수출입 물류 포워딩 쉽다"
              : "Intro - ShipDa - Digital freight forwarding service"
          }
          description={
            ShipdaCurrentLanguage.currentLanguage === "ko"
              ? "해상운송, 항공운송을 진행하는 수출입 고객을 위한 고객지원 페이지입니다."
              : "Providing support for ShipDa customers requiring ocean FCL/LCL and AIR import/export freight forwarding services."
          }
        />
      ) : (
        <Seo
          path="/support/guide"
          title={
            ShipdaCurrentLanguage.currentLanguage === "ko"
              ? "가이드 - 디지털 수출입 물류 포워딩 쉽다"
              : "Guide - ShipDa - Digital freight forwarding service"
          }
          description={
            ShipdaCurrentLanguage.currentLanguage === "ko"
              ? "해상, 항공수출입 운송을 더 쉽게, 디지털 포워더 쉽다를 이용하는 방법을 안내합니다."
              : "Comprehensive guide for Shipda customers, featuring detailed tips to optimize the use of Shipda's digital freight forwarding services."
          }
        />
      )}
      <CommonStyled.mainContainer>
        <SupportBreadcrumb />

        <BoardPageTitle titleStyle="default">
          {t("support-new:공통_가이드")}
        </BoardPageTitle>

        <BoardSection
          title={t("support-new:공통_이용방법")}
          moreHref="/support/guide/use"
          listData={useList}
        />
        <BoardSection
          title={t("support-new:공통_무역용어_운송프로세스")}
          moreHref="/support/guide/tradeterms"
          listData={tradeTermsList}
        />
        <BoardSection
          title="FAQ"
          moreHref="/support/guide/faq"
          listData={faqList}
        />
      </CommonStyled.mainContainer>

      <SupportSideMenu boardType="guide" />
    </SupportLayout>
  );
}
